/**
 * Read aloud (accessibility): speaks a game's prompt and its choices with the browser's own voices
 * (Web Speech API). Per device and off by default; the TV never reads unless asked to.
 *
 * Browsers only start speech from a user gesture the first time (iOS Safari strictly, Chrome with "not-allowed"):
 * `unlock()` is called on every tap. Text that could not start yet is kept as `pending` and spoken on the next
 * tap; once one utterance has started, later ones (new questions) play on their own for the rest of the session.
 */

/** The parts of SpeechSynthesisVoice the picker needs (so tests can pass plain objects). */
export interface VoiceLike {
  name: string;
  lang: string;
  voiceURI: string;
  localService?: boolean;
  default?: boolean;
}

/** Novelty and joke voices (macOS / iOS) that are hard to follow. Never picked automatically, never listed. */
const NOVELTY = [
  'albert', 'bad news', 'bahh', 'bells', 'boing', 'bubbles', 'cellos', 'deranged', 'good news', 'hysterical', 'jester',
  'organ', 'superstar', 'trinoids', 'whisper', 'wobble', 'zarvox', 'junior', 'ralph', 'kathy', 'princess', 'fred',
];
/** Older formant voices (Eloquence): understandable but robotic, so they rank last. */
const ROBOTIC = ['eddy', 'flo', 'grandma', 'grandpa', 'reed', 'rocko', 'sandy', 'shelley', 'espeak', 'robot'];
/** Names that signal a modern, natural-sounding voice, with a weight each. */
const QUALITY: [RegExp, number][] = [
  [/\bnatural\b/i, 40],
  [/\bneural\b/i, 40],
  [/\bpremium\b/i, 34],
  [/\benhanced\b/i, 30],
  [/\bsiri\b/i, 28],
  [/\bonline\b/i, 12],
  [/^google\b/i, 22],
  [/\b(aria|jenny|guy|ava|andrew|emma|brian|michelle)\b/i, 12],
  [/\bsamantha\b/i, 20],
  [/\b(allison|karen|daniel|moira|tessa|serena|alex)\b/i, 8],
];

const words = (s: string) => s.toLowerCase().split(/[^a-z]+/).filter(Boolean);
const hasWord = (name: string, list: string[]) => {
  const w = words(name);
  return list.some((n) => (n.includes(' ') ? name.toLowerCase().includes(n) : w.includes(n)));
};

export function isNoveltyVoice(v: VoiceLike): boolean {
  return hasWord(v.name, NOVELTY);
}

const base = (lang: string) => lang.toLowerCase().replace('_', '-').split('-')[0] ?? '';
const norm = (lang: string) => lang.toLowerCase().replace('_', '-');

/** How good a voice is likely to sound for casual, clear speech in `lang` (higher is better; -Infinity = never). */
export function voiceScore(v: VoiceLike, lang: string): number {
  if (isNoveltyVoice(v)) return -Infinity;
  if (base(v.lang) !== base(lang)) return -Infinity;
  let s = norm(v.lang) === norm(lang) ? 20 : 0;
  for (const [re, w] of QUALITY) if (re.test(v.name)) s += w;
  if (hasWord(v.name, ROBOTIC)) s -= 40;
  if (/compact/i.test(v.name) || /compact/i.test(v.voiceURI)) s -= 15;
  if (v.default) s += 4;
  if (v.localService) s += 1;
  return s;
}

/** Voices in the device language (no novelty voices), best-sounding first. Falls back to English, then to all. */
export function rankVoices(voices: readonly VoiceLike[], lang: string): VoiceLike[] {
  const rank = (l: string) =>
    voices
      .map((v, i) => ({ v, i, s: voiceScore(v, l) }))
      .filter((x) => x.s > -Infinity)
      .sort((a, b) => b.s - a.s || a.i - b.i)
      .map((x) => x.v);
  const own = rank(lang);
  if (own.length) return own;
  const en = rank('en-US');
  return en.length ? en : voices.filter((v) => !isNoveltyVoice(v));
}

/** The voice to use: the saved one if it is still installed, otherwise the best-ranked one. */
export function pickVoice(voices: readonly VoiceLike[], lang: string, savedURI?: string | null): VoiceLike | null {
  if (savedURI) {
    const saved = voices.find((v) => v.voiceURI === savedURI);
    if (saved) return saved;
  }
  return rankVoices(voices, lang)[0] ?? null;
}

/** "A: Jaws." for each choice, in order. `none` labels an option without a letter ("Or: Not an actual quote"). */
export function optionsSpeech(options: { id: string; text: string; letter?: boolean }[]): string[] {
  return options.map((o) => (o.letter === false ? `Or: ${o.text}.` : `${o.id}: ${o.text}.`).replace(/([.?!])\.$/, '$1'));
}

/** Cleans text for a voice: the screen's emphasis words ("EXACT") read as words, not spelled; no stray symbols. */
export function speakable(text: string): string {
  return text
    .replace(/\b(EXACT|NOT|ANY)\b/g, (w) => w.toLowerCase())
    .replace(/[“”]/g, '"')
    .replace(/[_*#]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export interface ReadAloudConfig {
  enabled: boolean;
  /** Saved voice (null = pick the best one automatically). */
  voiceURI: string | null;
  /** 0.8–1.2. */
  rate: number;
}

interface SynthLike {
  speak(u: SpeechSynthesisUtterance): void;
  cancel(): void;
  getVoices(): SpeechSynthesisVoice[];
  speaking: boolean;
  pending?: boolean;
  addEventListener?(type: 'voiceschanged', fn: () => void): void;
}
interface SpeechEnv {
  synth: SynthLike;
  Utterance: new (text?: string) => SpeechSynthesisUtterance;
  lang: string;
}

function browserEnv(): SpeechEnv | null {
  if (typeof window === 'undefined') return null;
  const synth = (window as { speechSynthesis?: SynthLike }).speechSynthesis;
  const Utterance = (window as { SpeechSynthesisUtterance?: SpeechEnv['Utterance'] }).SpeechSynthesisUtterance;
  if (!synth || !Utterance) return null;
  return { synth, Utterance, lang: (typeof navigator !== 'undefined' && navigator.language) || 'en-US' };
}

export const clampRate = (r: number) => Math.min(1.2, Math.max(0.8, Number.isFinite(r) ? Math.round(r * 10) / 10 : 1));

export class ReadAloud {
  private cfg: ReadAloudConfig = { enabled: false, voiceURI: null, rate: 1 };
  private listeners = new Set<() => void>();
  private envCache: SpeechEnv | null | undefined;
  private voiceList: VoiceLike[] = [];
  /** Text that has not started yet (blocked until a tap). */
  private pending: string[] | null = null;
  /** One utterance has started on this page: later ones need no tap. */
  started = false;
  private generation = 0;

  constructor(private env?: () => SpeechEnv | null) {}

  private get e(): SpeechEnv | null {
    if (this.envCache === undefined) {
      this.envCache = (this.env ?? browserEnv)();
      const e = this.envCache;
      if (e) {
        this.refreshVoices();
        e.synth.addEventListener?.('voiceschanged', () => this.refreshVoices(true));
      }
    }
    return this.envCache;
  }

  get supported(): boolean {
    return !!this.e;
  }
  get config(): ReadAloudConfig {
    return this.cfg;
  }
  get lang(): string {
    return this.e?.lang ?? 'en-US';
  }

  configure(patch: Partial<ReadAloudConfig>) {
    const next = { ...this.cfg, ...patch };
    next.rate = clampRate(next.rate);
    const turnedOff = this.cfg.enabled && !next.enabled;
    this.cfg = next;
    if (turnedOff) this.cancel();
    this.emit();
  }

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => void this.listeners.delete(fn);
  };
  getSnapshot = () => this.cfg;
  private emit() {
    for (const l of this.listeners) l();
  }

  /**
   * Re-reads the installed voices. Only the browser's voiceschanged event notifies subscribers: a lookup
   * from voices() / currentVoice() happens while a component renders, and notifying there would make it
   * re-render itself forever on a device with no voices at all.
   */
  private refreshVoices(notify = false) {
    const e = this.envCache;
    if (!e) return;
    try {
      this.voiceList = e.synth.getVoices().map((v) => v);
    } catch {
      this.voiceList = [];
    }
    if (notify) this.emit();
  }

  /** Installed voices for the device language, best first. */
  voices(): VoiceLike[] {
    if (!this.e) return [];
    if (!this.voiceList.length) this.refreshVoices();
    return rankVoices(this.voiceList, this.lang);
  }

  currentVoice(): VoiceLike | null {
    if (!this.e) return null;
    if (!this.voiceList.length) this.refreshVoices();
    return pickVoice(this.voiceList, this.lang, this.cfg.voiceURI);
  }

  /**
   * Speaks the parts in order (each its own utterance, so there is a natural pause between the quote and each
   * choice), replacing anything already being read. Does nothing while read aloud is off unless `force`.
   */
  speak(parts: string | string[], { force = false } = {}) {
    const e = this.e;
    if (!e || (!this.cfg.enabled && !force)) return;
    const list = (Array.isArray(parts) ? parts : [parts]).map(speakable).filter(Boolean);
    this.cancel();
    if (!list.length) return;
    const gen = ++this.generation;
    this.pending = list;
    const voice = this.currentVoice() as SpeechSynthesisVoice | null;
    for (const text of list) {
      const u = new e.Utterance(text);
      if (voice) u.voice = voice;
      u.lang = voice?.lang ?? e.lang;
      u.rate = this.cfg.rate;
      u.pitch = 1;
      u.volume = 1;
      u.onstart = () => {
        this.started = true;
        if (gen === this.generation) this.pending = null;
      };
      u.onerror = (ev) => {
        // Blocked until a tap: keep the text for the next one.
        if ((ev as SpeechSynthesisErrorEvent).error === 'not-allowed') this.started = false;
      };
      try {
        e.synth.speak(u);
      } catch {
        /* a broken speech engine never breaks the game */
      }
    }
  }

  /** Stops anything being read (question changed, phase ended, mode turned off). */
  cancel() {
    this.generation++;
    this.pending = null;
    try {
      this.e?.synth.cancel();
    } catch {
      /* ignore */
    }
  }

  /**
   * Call from a user gesture. iOS only lets a page speak after it has spoken inside a tap: speaks the text
   * that was blocked, or (the first time) a silent utterance, so auto-read works for the rest of the session.
   */
  unlock() {
    const e = this.e;
    if (!e || !this.cfg.enabled) return;
    if (this.pending && !e.synth.speaking) {
      this.speak(this.pending);
      return;
    }
    if (this.started || e.synth.speaking) return;
    try {
      const u = new e.Utterance(' ');
      u.volume = 0;
      u.onstart = () => {
        this.started = true;
      };
      e.synth.speak(u);
    } catch {
      /* ignore */
    }
  }
}

/** This device's read-aloud voice (configured by the app from the player's settings). */
export const readAloud = new ReadAloud();
